/**
 * Mundalo - canonical visa financial thresholds (frontend mirror).
 *
 * MUST stay in lockstep with /app/backend/content/visa_thresholds.py.
 * Anytime you edit one, edit the other. There is no runtime sync - this
 * file is imported directly at build time so every tool, calculator,
 * intake, and marketing surface displays IDENTICAL figures.
 *
 * All rendered numbers MUST be prefixed with "approximately" via the
 * exported `approx()` helper and paired with STANDARD_DISCLAIMER.
 */

// ISO-8601 date the current figures were verified. Bump every 90 days.
export const VERIFIED_AT = '2026-07-14';
export const VERIFIED_LABEL = 'verified as of July 2026';

/** @typedef {'temporary_resident' | 'permanent_resident'} VisaId */

export const TEMPORARY_RESIDENT = Object.freeze({
  visa_id: 'temporary_resident',
  label: 'Temporary Resident (Residente Temporal)',
  monthly_income_usd: 4400,
  savings_usd: 74000,
  per_dependent_add_monthly_usd: null,
  per_dependent_add_savings_usd: null,
  source_date: VERIFIED_AT,
  source_note:
    'Based on the 2026 UMA of 117.31 MXN. Approximately 300× UMA per month for income, or approximately 5,000× UMA in savings.',
});

export const PERMANENT_RESIDENT = Object.freeze({
  visa_id: 'permanent_resident',
  label: 'Permanent Resident (Residente Permanente)',
  monthly_income_usd: 7400,
  savings_usd: 295000,
  per_dependent_add_monthly_usd: null,
  per_dependent_add_savings_usd: null,
  source_date: VERIFIED_AT,
  source_note:
    'Based on the 2026 UMA of 117.31 MXN. Approximately 500× UMA per month for income, or approximately 20,000× UMA in savings.',
});

export const VISA_THRESHOLDS = Object.freeze({
  temporary_resident: TEMPORARY_RESIDENT,
  permanent_resident: PERMANENT_RESIDENT,
});

/**
 * Render a USD amount with the mandatory "approximately" prefix.
 * Example: approx(4400) → "approximately $4,400"
 */
export const approx = (usd) => `approximately $${Number(usd).toLocaleString('en-US')}`;

/** Standard footnote appended wherever a threshold is displayed. */
export const STANDARD_DISCLAIMER =
  `Figures are approximate - varies by consulate and exchange rate. ${VERIFIED_LABEL}.`;

/** Standard per-applicant labeling clause. */
export const PER_APPLICANT_LABEL = 'per applicant';

/** Dependent disclaimer shown until per-dependent numbers are confirmed. */
export const DEPENDENT_DISCLAIMER =
  'Bringing a spouse or dependents? Requirements increase for each dependent - amounts vary by consulate. Your Blueprint calculates this for your specific household.';

/**
 * One-line renderable string for a threshold. Used in tool result cards
 * and email copy.
 * Example: "approximately $4,400/month per applicant, or approximately
 * $74,000 in savings per applicant"
 */
export function renderThresholdLine(visaId) {
  const t = VISA_THRESHOLDS[visaId];
  if (!t) return '';
  return `${approx(t.monthly_income_usd)}/month ${PER_APPLICANT_LABEL}, or ${approx(t.savings_usd)} in savings ${PER_APPLICANT_LABEL}`;
}
